import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {distinctUntilChanged, map} from 'rxjs/operators';
import {IModuleSchemaStoreService} from '../main/models/IModuleSchemaStoreService';
import {IModuleSchema} from '@shared/models/moduleSchema';
import {UtilsService} from '@shared/utils/util';
import {StorageService} from '../shared/services/storage.service';
import {
  initialSchemaStoreState,
  SchemaStoreState,
  SchemaStoreStatus,
} from './schema-store-state';

@Injectable({
  providedIn: 'root'
})
export class ModuleSchemaStoreService implements IModuleSchemaStoreService {

  /** tenantId::schemaVersion → module id → schema (session-scoped instance cache; AC-29). */
  private cache: { [tenantKey: string]: { [id: string]: IModuleSchema } } = {};
  private activeTenantKey: string | null = null;

  private readonly stateSubject =
    new BehaviorSubject<SchemaStoreState<IModuleSchema>>(initialSchemaStoreState());

  readonly state$: Observable<SchemaStoreState<IModuleSchema>> = this.stateSubject.asObservable();
  readonly status$: Observable<SchemaStoreStatus> = this.state$.pipe(
    map(s => s.status),
    distinctUntilChanged(),
  );
  readonly schemas$: Observable<IModuleSchema[]> = this.state$.pipe(
    map(s => s.items),
  );
  readonly error$: Observable<unknown | null> = this.state$.pipe(
    map(s => s.error),
    distinctUntilChanged(),
  );

  constructor(private storage: StorageService) {
  }

  getAll(): IModuleSchema[] {
    if (this.stateSubject.value.status === 'error') {
      return [];
    }
    const key = this.activeTenantKey;
    if (!key || !this.cache[key]) {
      return [];
    }
    return UtilsService.sortModules(Object.values(this.cache[key]));
  }

  get status(): SchemaStoreStatus {
    return this.stateSubject.value.status;
  }

  /** Mark store as loading (e.g. before HTTP fetch). */
  markLoading(): void {
    this.stateSubject.next({
      status: 'loading',
      items: this.stateSubject.value.items,
      error: null,
    });
  }

  /**
   * Atomically replace the active tenant bucket with the given list (AC-37).
   * Does not merge with prior entries for that tenant key.
   */
  set(listSchemas: IModuleSchema[], tenantId?: string, schemaVersion?: string | number | null): void {
    const key = this.tenantKey(tenantId, schemaVersion);
    const bucket: { [id: string]: IModuleSchema } = {};
    (listSchemas || []).forEach(s => {
      if (s?.id_number != null) {
        bucket[s.id_number] = s;
      }
    });
    this.cache[key] = bucket;
    this.activeTenantKey = key;
    this.emitLoaded(key);
  }

  get(byName: string, tenantId?: string, schemaVersion?: string | number | null): IModuleSchema {
    return this.bucket(tenantId, schemaVersion)[byName];
  }

  clear(): void {
    const key = this.tenantKey();
    this.cache[key] = {};
    this.activeTenantKey = key;
    this.stateSubject.next(initialSchemaStoreState());
  }

  /** Drop all tenant buckets (logout / full session reset). */
  resetSession(): void {
    this.cache = {};
    this.activeTenantKey = null;
    this.stateSubject.next(initialSchemaStoreState());
  }

  markError(error: unknown): void {
    this.stateSubject.next({
      status: 'error',
      items: [],
      error,
    });
  }

  private emitLoaded(key: string): void {
    const items = UtilsService.sortModules(Object.values(this.cache[key] || {}));
    this.stateSubject.next({status: 'loaded', items, error: null});
  }

  private bucket(tenantId?: string, schemaVersion?: string | number | null): { [id: string]: IModuleSchema } {
    const key = this.tenantKey(tenantId, schemaVersion);
    this.activeTenantKey = key;
    if (!this.cache[key]) {
      this.cache[key] = {};
    }
    return this.cache[key];
  }

  private tenantKey(tenantId?: string, schemaVersion?: string | number | null): string {
    const tid = tenantId ?? this.storage.getTenantId();
    const ver = schemaVersion ?? this.storage.getSession()?.schemaVersion ?? '';
    return `${tid}::${ver}`;
  }

}
